import React from "react";
import { useIntl } from "react-intl";
import Card from "../Card/Card";
import { Icon } from "@chakra-ui/react";
import { LuUsers } from "react-icons/lu";
import { IoMdCheckmarkCircleOutline } from "react-icons/io";
import { MdExpandMore } from "react-icons/md";
import { TbTrendingUp } from "react-icons/tb";
import { Box, Text, Flex, Progress } from "@chakra-ui/react";

interface SharedWithAllCustomersCardProps {
  sharedByAdminCount: number;
  sharedByAdminTimestamp: number;
  acceptanceRate: number;
  responseCount?: number;
}

export default function SharedWithAllCustomersCard({
  sharedByAdminCount,
  sharedByAdminTimestamp,
  acceptanceRate,
  responseCount = 1,
}: SharedWithAllCustomersCardProps) {
  const intl = useIntl();

  return (
    <Card 
      mb={3} 
      bg="white" 
      boxShadow="0px 1px 3px rgba(0, 0, 0, 0.1)" 
      border="1px solid #e2e8f0" 
      borderRadius="10px" 
      p={3.5}
    >
      {/* Header Section */}
      <Flex alignItems="center" justifyContent="space-between" mb={3}>
        <Flex alignItems="center" gap={2.5}>
          <Box
            bg="#dcfce7"
            borderRadius="full"
            boxSize="36px"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Icon as={IoMdCheckmarkCircleOutline} color="#16a34a" boxSize={6} />
          </Box>
          <Box>
            <Text
              fontWeight="600"
              color="#111827"
              fontSize="15px"
              lineHeight="19px"
            >
              {intl.formatMessage({
                id: "sharing.sharedWithAllCustomers",
                defaultMessage: "Shared with all customers",
              })}
            </Text>
            <Box
              as="span"
              bg="#dcfce7"
              color="#16a34a"
              fontWeight="500"
              px={2}
              py={0.5}
              borderRadius="5px"
              fontSize="11px"
              mt={0.5}
              display="inline-block"
            >
              Active
            </Box>
          </Box>
        </Flex>
        <Icon as={MdExpandMore} color="#6b7280" boxSize={4.5} />
      </Flex>

      {/* Metrics Section */}
      <Flex gap={3} mb={3}>
        {/* Total Reach */}
        <Box
          flex="1"
          bg="#eff6ff"
          borderRadius="7px"
          p={2.5}
          border="1px solid #dbeafe"
        >
          <Flex alignItems="center" gap={2} mb={1}>
            <Icon as={LuUsers} color="#3b82f6" boxSize={3.5} />
            <Text color="#6b7280" fontSize="11px" fontWeight="500">
              Total Reach
            </Text>
          </Flex>
          <Text color="#1e40af" fontSize="20px" fontWeight="700" lineHeight="24px">
            {sharedByAdminCount?.toLocaleString()}
          </Text>
        </Box>

        {/* Acceptance Rate */}
        <Box
          flex="1"
          bg="#f0fdf4"
          borderRadius="7px"
          p={2.5}
          border="1px solid #dcfce7"
        >
          <Flex alignItems="center" gap={2} mb={1}>
            <Icon as={TbTrendingUp} color="#16a34a" boxSize={3.5} />
            <Text color="#6b7280" fontSize="11px" fontWeight="500">
              Acceptance
            </Text>
          </Flex>
          <Text color="#15803d" fontSize="20px" fontWeight="700" lineHeight="24px">
            {acceptanceRate?.toFixed(2)}%
          </Text>
        </Box>
      </Flex>

      {/* Response Progress Section */}
      <Box mb={2.5}>
        <Flex alignItems="center" justifyContent="space-between" mb={1.5}>
          <Text color="#374151" fontSize="13px" fontWeight="500">
            Response Progress
          </Text>
          <Text color="#6b7280" fontSize="13px">
            {responseCount} of {sharedByAdminCount?.toLocaleString()}
          </Text>
        </Flex>
        <Progress 
          value={sharedByAdminCount > 0 ? (responseCount / sharedByAdminCount) * 100 : 0} 
          size="sm" 
          colorScheme="blue" 
          borderRadius="full"
          bg="#f3f4f6"
          height="7px"
        />
      </Box>

      {/* Timestamp */}
      <Flex alignItems="center" gap={2}>
        <Box boxSize="3.5px" bg="#6b7280" borderRadius="full" />
        <Text color="#6b7280" fontSize="13px">
          {sharedByAdminTimestamp
            ? getLocalizedTimeAgo(sharedByAdminTimestamp, intl)
            : ""}
        </Text>
      </Flex>
    </Card>
  );
}

const getLocalizedTimeAgo = (timestamp: number, intl: any) => {
  const now = Date.now();
  const diffMs = now - timestamp;
  const diffMinutes = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / (60 * 60000));
  const diffDays = Math.floor(diffMs / (24 * 60 * 60000));
  const diffWeeks = Math.floor(diffMs / (7 * 24 * 60 * 60000));
  const diffMonths = Math.floor(diffMs / (30 * 24 * 60 * 60000));

  let value = 0;
  let unit = "minute";
  if (diffMinutes < 60) {
    value = diffMinutes;
    unit = value === 1 ? "minute" : "minutes";
  } else if (diffHours < 24) {
    value = diffHours;
    unit = value === 1 ? "hour" : "hours";
  } else if (diffDays < 7) {
    value = diffDays;
    unit = value === 1 ? "day" : "days";
  } else if (diffWeeks < 4) {
    value = diffWeeks;
    unit = value === 1 ? "week" : "weeks";
  } else {
    value = diffMonths;
    unit = value === 1 ? "month" : "months";
  }

  // Fallback for translation keys
  const unitKeyMap: Record<string, string> = {
    minute: "minute",
    minutes: "subscription.minutes",
    hour: "hour",
    hours: "subscription.hours",
    day: "day",
    days: "subscription.days",
    week: "week",
    weeks: "weeks",
    month: "month",
    months: "months",
  };

  // Try to use translation key, fallback to unit
  const unitLabel = intl.formatMessage({
    id: unitKeyMap[unit] || unit,
    defaultMessage: unit,
  });

  // No translation key for 'ago', so use a default message
  return `${intl.locale === "ar" ? "منذ" : ""} ${value} ${unitLabel} ${
    intl.locale === "ar" ? "" : "ago"
  }`;
};
